import React from 'react';
import { Calendar, Clock, MapPin, Video as VideoIcon, ExternalLink, FileText } from 'lucide-react';
import './ViewerComponents.css';

interface ILTSViewerProps {
  iltsType: 'Online' | 'Offline';
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  eventVenue?: string;
  meetUrl?: string;
  description?: string;
}

export const ILTSViewer: React.FC<ILTSViewerProps> = ({
  iltsType,
  startDate,
  startTime,
  endDate,
  endTime,
  eventVenue,
  meetUrl,
  description,
}) => {
  return (
    <div className="s-admin-ilts-viewer">
      <div className="s-admin-ilts-viewer__header">
        <div className="s-admin-ilts-viewer__header-icon">
          {iltsType === 'Online' ? (
            <VideoIcon size={32} className="s-admin-ilts-viewer__icon--online" />
          ) : (
            <MapPin size={32} className="s-admin-ilts-viewer__icon--offline" />
          )}
        </div>
        <div className="s-admin-ilts-viewer__header-text">
          <h2 className="s-admin-ilts-viewer__title">
            {iltsType === 'Online' ? 'Online Training Session' : 'In-Person Training Session'}
          </h2>
          <p className="s-admin-ilts-viewer__subtitle">
            {iltsType === 'Online'
              ? 'Join the live virtual session'
              : 'Attend the training at the venue'}
          </p>
        </div>
      </div>

      <div className="s-admin-ilts-viewer__content">
        <div className="s-admin-ilts-viewer__grid">
          {/* Schedule Section */}
          <div className="s-admin-ilts-viewer__section ilts-viewer__section--full">
            <h3 className="s-admin-ilts-viewer__section-title">
              <Calendar size={20} />
              Schedule
            </h3>
            <div className="s-admin-ilts-viewer__schedule-grid">
              {startDate && (
                <div className="s-admin-ilts-viewer__info-card">
                  <div className="s-admin-ilts-viewer__info-label">Start Date</div>
                  <div className="s-admin-ilts-viewer__info-value">
                    {new Date(startDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              )}

              {startTime && (
                <div className="s-admin-ilts-viewer__info-card">
                  <div className="s-admin-ilts-viewer__info-label">Start Time</div>
                  <div className="s-admin-ilts-viewer__info-value">
                    <Clock size={16} />
                    {startTime}
                  </div>
                </div>
              )}

              {endDate && (
                <div className="s-admin-ilts-viewer__info-card">
                  <div className="s-admin-ilts-viewer__info-label">End Date</div>
                  <div className="s-admin-ilts-viewer__info-value">
                    {new Date(endDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              )}

              {endTime && (
                <div className="s-admin-ilts-viewer__info-card">
                  <div className="s-admin-ilts-viewer__info-label">End Time</div>
                  <div className="s-admin-ilts-viewer__info-value">
                    <Clock size={16} />
                    {endTime}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location/Meeting Section */}
          {iltsType === 'Offline' && eventVenue && (
            <div className="s-admin-ilts-viewer__section ilts-viewer__section--full">
              <h3 className="s-admin-ilts-viewer__section-title">
                <MapPin size={20} />
                Venue
              </h3>
              <div className="s-admin-ilts-viewer__venue-card">
                <MapPin size={24} className="s-admin-ilts-viewer__venue-icon" />
                <div className="s-admin-ilts-viewer__venue-text">
                  <div className="s-admin-ilts-viewer__venue-label">Training Location</div>
                  <div className="s-admin-ilts-viewer__venue-address">{eventVenue}</div>
                </div>
              </div>
            </div>
          )}

          {iltsType === 'Online' && meetUrl && (
            <div className="s-admin-ilts-viewer__section ilts-viewer__section--full">
              <h3 className="s-admin-ilts-viewer__section-title">
                <VideoIcon size={20} />
                Meeting Link
              </h3>
              <div className="s-admin-ilts-viewer__meeting-card">
                <div className="s-admin-ilts-viewer__meeting-info">
                  <p className="s-admin-ilts-viewer__meeting-text">
                    Click the button below to join the virtual training session
                  </p>
                  <a
                    href={meetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="s-admin-ilts-viewer__meeting-url"
                  >
                    {meetUrl}
                  </a>
                </div>
                <button
                  onClick={() => window.open(meetUrl, '_blank')}
                  className="s-admin-ilts-viewer__join-btn"
                >
                  <ExternalLink size={20} />
                  Join Virtual Session
                </button>
              </div>
            </div>
          )}

          {/* Description */}
          {description && (
            <div className="s-admin-ilts-viewer__section ilts-viewer__section--full">
              <h3 className="s-admin-ilts-viewer__section-title">
                <FileText size={20} />
                Description
              </h3>
              <div className="s-admin-ilts-viewer__description">
                {description}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
